import { useState, useEffect, useCallback } from 'react'
import {
  adminLogin,
  adminLogout,
  isAdminAuthenticated,
  getUsers,
  getAllShows,
  getAllFinancials,
  getAnalyticsSummary,
  getRecentActivity,
  getSystemHealth,
  getUserDetail,
  getAdminApiKeys,
  getAdminApiUsage,
  type AdminUser,
} from '../../lib/adminApi'
import {
  AdminDashboardSkeleton,
  AdminUsersTableSkeletonRows,
  AdminTableBlockSkeleton,
  AdminActivityTabSkeleton,
  AdminSystemTabSkeleton,
  AdminUserDetailSkeleton,
} from '../../components/ui/Skeleton'

// ── Types ────────────────────────────────────────────────────────────

type Tab = 'overview' | 'users' | 'shows' | 'financials' | 'activity' | 'system' | 'api'

interface OverviewStats {
  totalUsers: number
  activeUsersToday: number
  totalShows: number
  upcomingShows: number
  totalRevenue: number
  totalSpend: number
  totalTeams: number
  newUsersThisWeek: number
  totalLinks: number
  totalTasks: number
}

interface ShowEntry {
  _id: string
  userId?: string
  userName?: string
  title: string
  venue?: string
  city?: string
  date: string
  ticket_count?: number
  tix_sold?: number
  syncSource?: string
  status?: string
}

interface FinancialRow {
  _id: string
  userId?: string
  userName?: string
  date: string
  type: 'revenue' | 'spend'
  amount: number
  description?: string
  source?: string
}

interface ActivityEntry {
  _id: string
  eventType: string
  section: string
  detail?: string
  actor?: { userName?: string; userEmail?: string }
  createdAt: string
}

interface SystemInfo {
  status: string
  uptime?: number
  dbConnected?: boolean
  version?: string
  environment?: string
  lastSync?: string
  pendingSyncs?: number
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatDate(d?: string): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatCurrency(n?: number): string {
  if (n == null) return '$0'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n)
}

function formatNumber(n?: number): string {
  if (n == null) return '0'
  return new Intl.NumberFormat('en-US').format(n)
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// ── Stat Card ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = 'gray' }: { label: string; value: string; sub?: string; color?: string }) {
  const colorMap: Record<string, string> = {
    indigo: 'border-indigo-200 bg-indigo-50/50',
    green: 'border-emerald-200 bg-emerald-50/50',
    purple: 'border-purple-200 bg-purple-50/50',
    amber: 'border-amber-200 bg-amber-50/50',
    rose: 'border-rose-200 bg-rose-50/50',
    sky: 'border-sky-200 bg-sky-50/50',
    gray: 'border-gray-200 bg-gray-50/50',
  }
  return (
    <div className={`rounded-xl border p-5 ${colorMap[color] || colorMap.gray}`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
  )
}

// ── Search Input ─────────────────────────────────────────────────────

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors"
      />
    </div>
  )
}

// ── Login Screen ─────────────────────────────────────────────────────

function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await adminLogin(email, password)
      onLogin()
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Invalid credentials'
      if (err?.response?.status === 403) {
        setError('This email is not authorized for admin access.')
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <img src="/favicon.png" alt="DIZEE" className="w-10 h-10 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900">Admin Console</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in with your admin credentials</p>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
              placeholder="admin@diz.ee"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
              placeholder="••••••••"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  )
}

// ── User Detail Modal ────────────────────────────────────────────────

function UserDetailModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getUserDetail(userId)
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [userId])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">User Detail</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5">
          {loading ? (
            <AdminUserDetailSkeleton />
          ) : !user ? (
            <p className="text-sm text-gray-500 text-center py-8">Could not load user details.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-500">
                    {(user.name || user.email || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">{user.name || user.username || '—'}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <Detail label="Role" value={user.professionRole || 'artist'} />
                <Detail label="Domain" value={user.domain || '—'} />
                <Detail label="Phone" value={user.phone || '—'} />
                <Detail label="Created" value={formatDate(user.createdAt)} />
                <Detail label="Last Login" value={formatDate(user.lastLogin || user.updatedAt)} />
                <Detail label="User ID" value={user._id} mono />
              </div>

              {user.shows && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Shows ({user.shows.length})</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {user.shows.map((s: any) => (
                      <div key={s._id || s.id} className="text-xs text-gray-600 flex justify-between">
                        <span className="truncate">{s.title || s.event_title || '—'}</span>
                        <span className="text-gray-400 ml-2 flex-shrink-0">{formatDate(s.date || s.perf_date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {user.team && user.team.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Team ({user.team.length})</p>
                  <div className="space-y-1">
                    {user.team.map((t: any) => (
                      <div key={t._id || t.email} className="text-xs text-gray-600 flex justify-between">
                        <span>{t.name || t.email}</span>
                        <span className="text-gray-400">{t.role || t.professionRole}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`text-gray-700 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</p>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN ADMIN PAGE
// ═══════════════════════════════════════════════════════════════════════

export default function AdminPage() {
  const [authed, setAuthed] = useState(isAdminAuthenticated)
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  if (!authed) {
    return <AdminLogin onLogin={() => setAuthed(true)} />
  }

  const handleLogout = () => {
    adminLogout()
    setAuthed(false)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src="/favicon.png" alt="DIZEE" className="w-7 h-7" />
            <span className="text-sm font-semibold text-gray-900">Admin Console</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto -mb-px">
            {(
              [
                { id: 'overview', label: 'Overview', icon: BarChartIcon },
                { id: 'users', label: 'Users', icon: UsersIcon },
                { id: 'shows', label: 'Shows', icon: TicketIcon },
                { id: 'financials', label: 'Financials', icon: DollarIcon },
                { id: 'activity', label: 'Activity', icon: BoltIcon },
                { id: 'system', label: 'System', icon: ServerIcon },
                { id: 'api', label: 'API', icon: CodeBracketIcon },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <tab.icon />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'shows' && <ShowsTab />}
        {activeTab === 'financials' && <FinancialsTab />}
        {activeTab === 'activity' && <ActivityTab />}
        {activeTab === 'system' && <SystemTab />}
        {activeTab === 'api' && <ApiTab />}
      </main>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// TAB: Overview
// ═══════════════════════════════════════════════════════════════════════

function OverviewTab() {
  const [stats, setStats] = useState<OverviewStats | null>(null)
  const [recentUsers, setRecentUsers] = useState<AdminUser[]>([])
  const [recentActivity, setRecentActivity] = useState<ActivityEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [summaryRes, usersRes, activityRes] = await Promise.allSettled([
          getAnalyticsSummary(),
          getUsers({ limit: 5 }),
          getRecentActivity({ limit: 8 }),
        ])

        if (summaryRes.status === 'fulfilled') setStats(summaryRes.value)
        if (usersRes.status === 'fulfilled') setRecentUsers(usersRes.value?.users || [])
        if (activityRes.status === 'fulfilled') setRecentActivity(activityRes.value?.activities || [])
      } catch {
        // partial data is fine
      }
      setLoading(false)
    }
    load()
  }, [])

  if (loading) return <AdminDashboardSkeleton />

  return (
    <div className="space-y-8">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Total Users" value={formatNumber(stats?.totalUsers)} color="indigo" />
        <StatCard label="Active Today" value={formatNumber(stats?.activeUsersToday)} sub="sessions" color="green" />
        <StatCard label="Total Shows" value={formatNumber(stats?.totalShows)} color="purple" />
        <StatCard label="Upcoming Shows" value={formatNumber(stats?.upcomingShows)} color="amber" />
        <StatCard label="New This Week" value={formatNumber(stats?.newUsersThisWeek)} sub="users" color="sky" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total Revenue" value={formatCurrency(stats?.totalRevenue)} color="green" />
        <StatCard label="Total Spend" value={formatCurrency(stats?.totalSpend)} color="rose" />
        <StatCard label="Teams" value={formatNumber(stats?.totalTeams)} color="purple" />
        <StatCard label="Links" value={formatNumber(stats?.totalLinks)} color="sky" />
      </div>

      {/* Recent users + Activity side by side */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Recent Users</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {recentUsers.length === 0 ? (
              <p className="text-sm text-gray-500 p-5">No user data available.</p>
            ) : (
              recentUsers.map((u) => (
                <div key={u._id} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-500 flex-shrink-0">
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.name || u.username || u.email}</p>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(u.createdAt)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Recent Activity</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {recentActivity.length === 0 ? (
              <p className="text-sm text-gray-500 p-5">No activity data available.</p>
            ) : (
              recentActivity.map((a) => (
                <div key={a._id} className="px-5 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">{a.actor?.userName || a.actor?.userEmail || 'System'}</span>
                      {' '}{a.eventType.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{timeAgo(a.createdAt)}</span>
                  </div>
                  {a.detail && <p className="text-xs text-gray-500 mt-0.5 truncate">{a.detail}</p>}
                  <span className="inline-block mt-1 text-[10px] font-medium text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{a.section}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// TAB: Users
// ═══════════════════════════════════════════════════════════════════════

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const limit = 20

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getUsers({ search: search || undefined, page, limit })
      setUsers(res.users || [])
      setTotal(res.total || 0)
    } catch {
      setUsers([])
    }
    setLoading(false)
  }, [search, page])

  useEffect(() => { load() }, [load])

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1)
      setSearch(debouncedSearch)
    }, 350)
    return () => clearTimeout(t)
  }, [debouncedSearch])

  const totalPages = Math.ceil(total / limit)

  const roleBadge = (role?: string) => {
    const colors: Record<string, string> = {
      artist: 'bg-purple-50 text-purple-700 border-purple-200',
      manager: 'bg-gray-50 text-gray-700 border-gray-200',
      support: 'bg-gray-50 text-gray-600 border-gray-200',
      promoter: 'bg-amber-50 text-amber-700 border-amber-200',
    }
    const r = role || 'artist'
    return (
      <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-full border ${colors[r] || colors.artist}`}>
        {r}
      </span>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <SearchInput
            value={debouncedSearch}
            onChange={setDebouncedSearch}
            placeholder="Search users by name, email, or domain…"
          />
        </div>
        <span className="text-sm text-gray-500">{formatNumber(total)} total users</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Domain</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Created</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Last Active</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <AdminUsersTableSkeletonRows />
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-gray-500">No users found.</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {u.avatar ? (
                          <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                            {(u.name || u.email || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{u.name || u.username || '—'}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">{roleBadge(u.professionRole)}</td>
                    <td className="px-5 py-3 text-gray-600 hidden md:table-cell">{u.domain || '—'}</td>
                    <td className="px-5 py-3 text-gray-500 hidden lg:table-cell">{formatDate(u.createdAt)}</td>
                    <td className="px-5 py-3 text-gray-500 hidden lg:table-cell">{formatDate(u.lastLogin || u.updatedAt)}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => setSelectedUserId(u._id)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedUserId && <UserDetailModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// TAB: Shows
// ═══════════════════════════════════════════════════════════════════════

function ShowsTab() {
  const [shows, setShows] = useState<ShowEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const limit = 20

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getAllShows({ search: search || undefined, page, limit })
      setShows(res.shows || [])
      setTotal(res.total || 0)
    } catch {
      setShows([])
    }
    setLoading(false)
  }, [search, page])

  useEffect(() => { load() }, [load])

  const [debouncedSearch, setDebouncedSearch] = useState(search)
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(debouncedSearch) }, 350)
    return () => clearTimeout(t)
  }, [debouncedSearch])

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <SearchInput value={debouncedSearch} onChange={setDebouncedSearch} placeholder="Search shows by title, venue, city…" />
        </div>
        <span className="text-sm text-gray-500">{formatNumber(total)} total shows</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Show</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Artist</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets</th>
                <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Sold</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <AdminTableBlockSkeleton />
              ) : shows.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-gray-500">No shows found.</td></tr>
              ) : (
                shows.map((s) => (
                  <tr key={s._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900 truncate max-w-xs">{s.title}</p>
                      <p className="text-xs text-gray-500 truncate">{[s.venue, s.city].filter(Boolean).join(' — ')}</p>
                    </td>
                    <td className="px-5 py-3 text-gray-600 hidden md:table-cell truncate max-w-[140px]">{s.userName || '—'}</td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{formatDate(s.date)}</td>
                    <td className="px-5 py-3 text-right font-medium text-gray-900">{formatNumber(s.ticket_count)}</td>
                    <td className="px-5 py-3 text-right text-gray-600 hidden lg:table-cell">{s.tix_sold != null ? formatNumber(s.tix_sold) : '—'}</td>
                    <td className="px-5 py-3 hidden lg:table-cell">
                      {s.syncSource && (
                        <span className="inline-block px-2 py-0.5 text-[11px] font-medium rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                          {s.syncSource}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors">Previous</button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// TAB: Financials
// ═══════════════════════════════════════════════════════════════════════

function FinancialsTab() {
  const [entries, setEntries] = useState<FinancialRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const limit = 20

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getAllFinancials({ search: search || undefined, page, limit })
      setEntries(res.entries || [])
      setTotal(res.total || 0)
    } catch {
      setEntries([])
    }
    setLoading(false)
  }, [search, page])

  useEffect(() => { load() }, [load])

  const [debouncedSearch, setDebouncedSearch] = useState(search)
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(debouncedSearch) }, 350)
    return () => clearTimeout(t)
  }, [debouncedSearch])

  const totalPages = Math.ceil(total / limit)

  const totalRevenue = entries.filter((e) => e.type === 'revenue').reduce((s, e) => s + e.amount, 0)
  const totalSpend = entries.filter((e) => e.type === 'spend').reduce((s, e) => s + e.amount, 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard label="Page Revenue" value={formatCurrency(totalRevenue)} color="green" />
        <StatCard label="Page Spend" value={formatCurrency(totalSpend)} color="rose" />
        <StatCard label="Page Net" value={formatCurrency(totalRevenue - totalSpend)} color={totalRevenue - totalSpend >= 0 ? 'green' : 'rose'} />
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <SearchInput value={debouncedSearch} onChange={setDebouncedSearch} placeholder="Search financial entries…" />
        </div>
        <span className="text-sm text-gray-500">{formatNumber(total)} entries</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Source</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">User</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <AdminTableBlockSkeleton />
              ) : entries.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-gray-500">No entries found.</td></tr>
              ) : (
                entries.map((e) => (
                  <tr key={e._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{formatDate(e.date)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-full border ${
                        e.type === 'revenue'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-red-50 text-red-700 border-red-200'
                      }`}>
                        {e.type}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-medium text-gray-900">{formatCurrency(e.amount)}</td>
                    <td className="px-5 py-3 text-gray-600 hidden md:table-cell truncate max-w-[200px]">{e.description || '—'}</td>
                    <td className="px-5 py-3 text-gray-500 hidden lg:table-cell">{e.source || '—'}</td>
                    <td className="px-5 py-3 text-gray-500 hidden lg:table-cell truncate max-w-[120px]">{e.userName || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors">Previous</button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// TAB: Activity
// ═══════════════════════════════════════════════════════════════════════

function ActivityTab() {
  const [activities, setActivities] = useState<ActivityEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  useEffect(() => {
    setLoading(true)
    getRecentActivity({ page, limit: 30 })
      .then((res) => setActivities(res?.activities || []))
      .catch(() => setActivities([]))
      .finally(() => setLoading(false))
  }, [page])

  if (loading) return <AdminActivityTabSkeleton />

  const eventColor: Record<string, string> = {
    sync_request: 'bg-sky-100 text-sky-700',
    approval: 'bg-emerald-100 text-emerald-700',
    denial: 'bg-red-100 text-red-700',
    add: 'bg-indigo-100 text-indigo-700',
    edit: 'bg-amber-100 text-amber-700',
    delete: 'bg-red-100 text-red-700',
    submit: 'bg-purple-100 text-purple-700',
    sync: 'bg-sky-100 text-sky-700',
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">System Activity Log</h3>
          <span className="text-xs text-gray-500">Page {page}</span>
        </div>
        <div className="divide-y divide-gray-50">
          {activities.length === 0 ? (
            <p className="text-sm text-gray-500 p-5 text-center">No activity data available.</p>
          ) : (
            activities.map((a) => (
              <div key={a._id} className="px-5 py-3 flex items-start gap-3">
                <span className={`flex-shrink-0 mt-0.5 px-2 py-0.5 text-[10px] font-semibold rounded-full ${eventColor[a.eventType] || 'bg-gray-100 text-gray-600'}`}>
                  {a.eventType.replace(/_/g, ' ')}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{a.actor?.userName || a.actor?.userEmail || 'System'}</span>
                      {a.detail && <span className="text-gray-500"> — {a.detail}</span>}
                    </p>
                    <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{timeAgo(a.createdAt)}</span>
                  </div>
                  <span className="inline-block mt-1 text-[10px] font-medium text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{a.section}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex items-center justify-end px-5 py-3 border-t border-gray-100 gap-1">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors">Previous</button>
          <button onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">Next</button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// TAB: System
// ═══════════════════════════════════════════════════════════════════════

function SystemTab() {
  const [info, setInfo] = useState<SystemInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getSystemHealth()
      .then(setInfo)
      .catch(() => setInfo(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <AdminSystemTabSkeleton />

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatusCard
          label="API Status"
          value={info?.status === 'ok' ? 'Healthy' : info?.status || 'Unknown'}
          ok={info?.status === 'ok'}
        />
        <StatusCard
          label="Database"
          value={info?.dbConnected ? 'Connected' : 'Disconnected'}
          ok={info?.dbConnected ?? false}
        />
        <StatusCard
          label="Environment"
          value={info?.environment || 'unknown'}
        />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {info?.uptime != null && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Uptime</p>
            <p className="mt-2 text-lg font-semibold text-gray-900">{formatUptime(info.uptime)}</p>
          </div>
        )}
        {info?.version && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Version</p>
            <p className="mt-2 text-lg font-semibold text-gray-900 font-mono">{info.version}</p>
          </div>
        )}
        {info?.pendingSyncs != null && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Syncs</p>
            <p className="mt-2 text-lg font-semibold text-gray-900">{info.pendingSyncs}</p>
          </div>
        )}
        {info?.lastSync && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</p>
            <p className="mt-2 text-sm font-medium text-gray-900">{formatDate(info.lastSync)}</p>
          </div>
        )}
      </div>

      {!info && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500">
            System health endpoint not available. Make sure the backend exposes <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">GET /api/v1/admin/health</code>.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Links</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <QuickLink label="GA4 Dashboard" url="https://analytics.google.com" />
          <QuickLink label="MongoDB Atlas" url="https://cloud.mongodb.com" />
          <QuickLink label="Mailgun Dashboard" url="https://app.mailgun.com" />
          <QuickLink label="Stripe Dashboard" url="https://dashboard.stripe.com" />
          <QuickLink label="Bandsintown" url="https://manager.bandsintown.com" />
          <QuickLink label="Sentry (if configured)" url="https://sentry.io" />
        </div>
      </div>
    </div>
  )
}

function ApiTab() {
  const [keys, setKeys] = useState<unknown[]>([])
  const [usage, setUsage] = useState<{ requestsLast7Days?: number; byTier?: { _id: string; count: number }[] } | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  useEffect(() => {
    ;(async () => {
      try {
        const [k, u] = await Promise.all([getAdminApiKeys({ limit: 50 }), getAdminApiUsage()])
        setKeys(k.keys || [])
        setUsage(u)
      } catch {
        setErr('Could not load API data.')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  if (loading) {
    return <AdminTableBlockSkeleton />
  }

  return (
    <div className="space-y-6">
      {err && <p className="text-sm text-red-600">{err}</p>}
      {usage && (
        <div className="grid sm:grid-cols-2 gap-4">
          <StatCard label="Public API requests (7d)" value={String(usage.requestsLast7Days ?? 0)} color="indigo" />
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">By tier</p>
            <ul className="mt-2 text-sm text-gray-700">
              {(usage.byTier || []).map((t) => (
                <li key={t._id}>
                  {t._id}: {t.count}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">API keys (all users)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Prefix</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Tier</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {keys.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-gray-500">
                    No keys or endpoint unavailable.
                  </td>
                </tr>
              ) : (
                keys.map((row: any) => (
                  <tr key={row._id}>
                    <td className="px-5 py-3 font-mono text-xs">{row.keyPrefix}…</td>
                    <td className="px-5 py-3">{row.tier}</td>
                    <td className="px-5 py-3">{row.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function StatusCard({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className={`rounded-xl border p-5 ${
      ok === true ? 'border-emerald-200 bg-emerald-50/50' :
      ok === false ? 'border-red-200 bg-red-50/50' :
      'border-gray-200 bg-white'
    }`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <div className="mt-2 flex items-center gap-2">
        {ok != null && (
          <span className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
        )}
        <p className="text-lg font-semibold text-gray-900 capitalize">{value}</p>
      </div>
    </div>
  )
}

function QuickLink({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm text-gray-700 hover:text-gray-900"
    >
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
      {label}
    </a>
  )
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h ${m}m`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

// ── Shared UI ────────────────────────────────────────────────────────

// ── Tab Icons (inline SVGs to avoid extra deps) ──────────────────────

function CodeBracketIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  )
}

function BarChartIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function TicketIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  )
}

function DollarIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function BoltIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  )
}

function ServerIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
    </svg>
  )
}
